/**
 * SubscriptionGate — Wrapper component that gates content behind an active subscription.
 *
 * Usage:
 *   <SubscriptionGate>
 *     <DictationPage />
 *   </SubscriptionGate>
 *
 * If the user is not subscribed (and not in trial), shows a paywall overlay
 * instead of the children. Course browsing is NOT gated — only learning modes.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isSubscriptionActive } from '../../services/subscriptionService';
import './SubscriptionGate.css';

interface SubscriptionGateProps {
  children: React.ReactNode;
  /** Optional feature name for analytics / display */
  feature?: string;
}

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ children, feature }) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  // Not logged in — redirect to login
  if (!isAuthenticated || !user) {
    return (
      <div className="subscription-gate">
        <div className="subscription-gate-card">
          <div className="subscription-gate-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h2 className="subscription-gate-title">{t('gate.loginRequired', 'Sign in to start learning')}</h2>
          <p className="subscription-gate-description">
            {t('gate.loginDescription', 'Create a free account to get 7 days of full access to all learning features.')}
          </p>
          <div className="subscription-gate-actions">
            <button className="subscription-gate-primary" onClick={() => navigate('/login')}>
              {t('gate.signIn', 'Sign In')}
            </button>
            <button className="subscription-gate-secondary" onClick={() => navigate('/register')}>
              {t('gate.signUp', 'Create Account — 7 Days Free')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin or subscribed/trialing — show content
  if (user.role === 'admin' || isSubscriptionActive(user.subscriptionStatus)) {
    return <>{children}</>;
  }

  // Not subscribed — show paywall
  if (isDismissed) {
    // User dismissed but we still don't show the content
    navigate(-1);
    return null;
  }

  const trialExpired = user.subscriptionStatus === 'inactive';

  return (
    <div className="subscription-gate">
      <div className="subscription-gate-card">
        <div className="subscription-gate-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <h2 className="subscription-gate-title">
          {trialExpired
            ? t('gate.trialExpired', 'Your free trial has ended')
            : t('gate.subscriptionRequired', 'Premium Feature')
          }
        </h2>

        <p className="subscription-gate-description">
          {trialExpired
            ? t('gate.trialExpiredDescription', 'Subscribe to continue accessing all learning modes — vocabulary, comprehension, dictation, shadowing, and quizzes.')
            : t('gate.subscriptionDescription', 'This feature requires an active subscription.')
          }
        </p>

        {feature && (
          <div className="subscription-gate-feature-badge">
            {feature}
          </div>
        )}

        <div className="subscription-gate-benefits">
          <div className="subscription-gate-benefits-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <span>{t('gate.premiumIncludes', 'Premium includes')}</span>
          </div>
          <ul className="subscription-gate-benefits-list">
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{t('gate.benefit1', 'All 5 learning modes — unlimited')}</span>
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{t('gate.benefit2', 'AI-powered learning insights')}</span>
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{t('gate.benefit3', 'Personalized learning path')}</span>
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{t('gate.benefit4', 'Ad-free experience')}</span>
            </li>
          </ul>
        </div>

        <div className="subscription-gate-actions">
          <button className="subscription-gate-primary" onClick={() => navigate('/pricing')}>
            {t('gate.subscribe', 'Subscribe Now')}
          </button>
          <button className="subscription-gate-secondary" onClick={() => setIsDismissed(true)}>
            {t('gate.goBack', 'Go Back')}
          </button>
        </div>

        <p className="subscription-gate-price-hint">
          {t('gate.priceHint', 'Starting at $12.99/month · Cancel anytime')}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionGate;
